import { unitStatusLabel } from '@/lib/utils/status';

export const PROPERTY_LISTING_STATUSES = [
  'active',
  'conditional',
  'sold',
  'leased',
  'expired',
  'suspended',
  'terminated',
] as const;

export const PROPERTY_TRANSACTION_TYPES = ['sale', 'lease', 'assignment', 'sublease'] as const;

export const PROPERTY_VISIBILITY_STATES = ['draft', 'published', 'archived'] as const;

export const OCCUPANCY_TYPES = ['vacant', 'owner-occupied', 'tenant', 'model'] as const;

export const PROVINCE_OPTIONS = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
] as const;

export const STREET_TYPES = [
  'Ave',
  'Blvd',
  'Cir',
  'Cres',
  'Dr',
  'Gate',
  'Lane',
  'Pkwy',
  'Rd',
  'St',
  'Terr',
  'Way',
] as const;

export const STREET_DIRECTIONS = ['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'] as const;

export const GEO_SOURCES = ['manual', 'geocoded', 'imported'] as const;

export const PROPERTY_TYPES = [
  'condo',
  'residential',
  'commercial',
  'industrial',
  'office',
  'retail',
  'mixed_use',
  'land',
  'multi_family',
] as const;

export const PROPERTY_SUB_TYPES_BY_TYPE: Record<
  (typeof PROPERTY_TYPES)[number],
  ReadonlyArray<string>
> = {
  condo: ['condo_apartment', 'condo_townhouse', 'loft', 'other'],
  residential: ['detached', 'semi_detached', 'townhouse', 'duplex', 'triplex', 'fourplex', 'other'],
  commercial: [
    'office',
    'retail',
    'industrial',
    'warehouse',
    'medical',
    'development_site',
    'other',
  ],
  industrial: ['industrial', 'warehouse', 'development_site', 'other'],
  office: ['office', 'development_site', 'other'],
  retail: ['retail', 'development_site', 'other'],
  mixed_use: ['multi_family', 'retail', 'office', 'other'],
  land: ['land', 'development_site', 'agricultural', 'other'],
  multi_family: ['duplex', 'triplex', 'fourplex', 'multi_family', 'other'],
};

export const OWNERSHIP_TYPES = ['freehold', 'condominium', 'cooperative', 'leasehold'] as const;

export const TENURE_TYPES = ['freehold', 'condo', 'co_op', 'timeshare'] as const;

export const INTERIOR_AREA_UNITS = ['sqft', 'sqm'] as const;

export const BASEMENT_TYPES = [
  'none',
  'unfinished',
  'partially_finished',
  'finished',
  'crawl_space',
  'walk_out',
] as const;

export const FOUNDATION_TYPES = ['poured_concrete', 'block', 'stone', 'slab', 'pilings'] as const;

export const ROOF_TYPES = ['asphalt', 'metal', 'shingle', 'flat', 'tile', 'other'] as const;

export const FRONTAGE_TYPES = ['river', 'lake', 'golf_course', 'park', 'street'] as const;

export const WATER_SOURCES = ['municipal', 'well', 'lake_river', 'cistern'] as const;

export const SEWER_TYPES = ['sanitary', 'storm', 'septic', 'holding_tank', 'unknown'] as const;

export const MAINTENANCE_FEE_FREQUENCIES = ['monthly', 'quarterly', 'annually'] as const;

export const DEPOSIT_DUE_SCHEDULES = [
  'lease_signing',
  'thirty_days',
  'sixty_days',
  'other',
] as const;

export const MLS_BOARD_OPTIONS = [
  { value: 'TRREB', label: 'TRREB' },
  { value: 'CREA', label: 'CREA' },
  { value: 'OREB', label: 'Ottawa Real Estate Board' },
  { value: 'REBGV', label: 'Real Estate Board of Greater Vancouver' },
  { value: 'VREB', label: 'Victoria Real Estate Board' },
  { value: 'Other', label: 'Other Board' },
] as const;

export const UNIT_TYPES = [
  'residential',
  'commercial',
  'office',
  'retail',
  'industrial',
  'storage',
  'parking',
] as const;

export const UNIT_STATUSES = [
  'available',
  'application_pending',
  'leased',
  'sold',
  'coming_soon',
  'unavailable',
] as const;

export const UNIT_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  ...UNIT_STATUSES.map((status) => ({ value: status, label: unitStatusLabel(status) })),
] as const;

export const RENT_FREQUENCIES = ['monthly', 'quarterly', 'annually', 'weekly', 'daily'] as const;

export const FURNISHED_STATUSES = ['yes', 'partially', 'no'] as const;

export const BALCONY_TYPES = ['none', 'open', 'enclosed', 'terrace', 'patio'] as const;

export const UNIT_EXPOSURES = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

export const LAUNDRY_TYPES = ['in_suite', 'shared', 'none'] as const;

export const PETS_POLICIES = ['yes', 'restricted', 'no'] as const;

export const FLOORING_OPTIONS = [
  'hardwood',
  'laminate',
  'engineered_hardwood',
  'tile',
  'vinyl',
  'broadloom',
  'concrete',
  'stone',
] as const;

export const HEATING_TYPES = [
  'forced_air',
  'radiant',
  'boiler',
  'heat_pump',
  'baseboard',
  'electric',
  'gas',
  'other',
] as const;

export const HEATING_FUELS = [
  'natural_gas',
  'electric',
  'oil',
  'propane',
  'geo_thermal',
  'other',
] as const;

export const COOLING_TYPES = [
  'central_air',
  'wall_unit',
  'window_unit',
  'ductless',
  'none',
  'other',
] as const;

export const GENERAL_FEATURE_OPTIONS = [
  'gym',
  'pool',
  'concierge',
  'party_room',
  'media_room',
  'meeting_room',
  'visitor_parking',
  'bike_storage',
  'rooftop_deck',
  'bbq_area',
] as const;

export const SECURITY_FEATURE_OPTIONS = [
  'security_guard',
  'controlled_entry',
  'cctv',
  'intercom',
  'key_fob',
] as const;

export const ACCESSIBILITY_FEATURE_OPTIONS = [
  'barrier_free',
  'wheelchair_access',
  'elevator',
  'visual_alarms',
  'grab_bars',
] as const;

export const LOT_FEATURE_OPTIONS = [
  'corner_site',
  'cul_de_sac',
  'ravine',
  'waterfront',
  'golf_course',
  'park',
  'treed',
] as const;

export const UNIT_HIGHLIGHT_OPTIONS = [
  'premium_view',
  'high_ceiling',
  'upgraded_finishes',
  'smart_home',
  'pet_friendly',
  'close_to_transit',
  'flex_space',
] as const;

export type PropertyListingStatus = (typeof PROPERTY_LISTING_STATUSES)[number];
export type PropertyTransactionType = (typeof PROPERTY_TRANSACTION_TYPES)[number];
export type PropertyVisibilityState = (typeof PROPERTY_VISIBILITY_STATES)[number];
export type OccupancyType = (typeof OCCUPANCY_TYPES)[number];
export type ProvinceCode = (typeof PROVINCE_OPTIONS)[number]['value'];
export type StreetType = (typeof STREET_TYPES)[number];
export type StreetDirection = (typeof STREET_DIRECTIONS)[number];
export type GeoSource = (typeof GEO_SOURCES)[number];
export type PropertyType = (typeof PROPERTY_TYPES)[number];
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];
export type TenureType = (typeof TENURE_TYPES)[number];
export type InteriorAreaUnit = (typeof INTERIOR_AREA_UNITS)[number];
export type BasementType = (typeof BASEMENT_TYPES)[number];
export type FoundationType = (typeof FOUNDATION_TYPES)[number];
export type RoofType = (typeof ROOF_TYPES)[number];
export type FrontageType = (typeof FRONTAGE_TYPES)[number];
export type WaterSource = (typeof WATER_SOURCES)[number];
export type SewerType = (typeof SEWER_TYPES)[number];
export type MaintenanceFeeFrequency = (typeof MAINTENANCE_FEE_FREQUENCIES)[number];
export type DepositDueSchedule = (typeof DEPOSIT_DUE_SCHEDULES)[number];
export type MlsBoardCode = (typeof MLS_BOARD_OPTIONS)[number]['value'];
export type UnitType = (typeof UNIT_TYPES)[number];
export type UnitStatus = (typeof UNIT_STATUSES)[number];
export type RentFrequency = (typeof RENT_FREQUENCIES)[number];
export type FurnishedStatus = (typeof FURNISHED_STATUSES)[number];
export type BalconyType = (typeof BALCONY_TYPES)[number];
export type UnitExposure = (typeof UNIT_EXPOSURES)[number];
export type LaundryType = (typeof LAUNDRY_TYPES)[number];
export type PetsPolicy = (typeof PETS_POLICIES)[number];
